/**
 * Proje ayarları ve kullanıcı tercihleri
 * Bu dosya kullanıcının proje başında seçtiği global ayarları içerir
 */

// ===============================
// PROJECT TYPES (Proje Tipleri)
// ===============================
// Not: Export değil — sadece bu dosya içi self-reference (DEFAULT_PROJECT_SETTINGS).
// Dışa açık `PROJECT_TYPES` için: src/constants/projectTypes.js (CAD editör tipleri).
const PROJECT_TYPES = {
  RESIDENTIAL: 'residential',      // Konut
  COMMERCIAL: 'commercial',        // Ticari
  INDUSTRIAL: 'industrial',        // Endüstriyel
  MIXED_USE: 'mixed_use'          // Karma Kullanım
};

// ===============================
// BUILDING TYPES (Yapı Tipleri)
// ===============================
const BUILDING_TYPES = {
  APARTMENT: 'apartment',          // Apartman
  VILLA: 'villa',                 // Villa
  OFFICE: 'office',               // Ofis
  SHOP: 'shop',                   // Mağaza
  WAREHOUSE: 'warehouse',         // Depo
  FACTORY: 'factory'              // Fabrika
};

// ===============================
// USAGE PURPOSES (Kullanım Amaçları)
// ===============================
const USAGE_PURPOSES = {
  MESKEN: 'Mesken',               // Konut
  ISYERI: 'İşYeri',               // İş Yeri
  GT: 'Müşterek',                  // Genel Tüketim (Müşterek)
  ASANSOR: 'Asansör'              // Asansör
};

// ===============================
// ELECTRICAL STANDARDS (Elektrik Standartları)
// ===============================
const ELECTRICAL_STANDARDS = {
  IEC: 'IEC',                     // International Electrotechnical Commission
  IEEE: 'IEEE',                   // Institute of Electrical and Electronics Engineers
  TSE: 'TSE'                      // Türk Standartları Enstitüsü
};

// ===============================
// DEFAULT PROJECT SETTINGS
// ===============================
export const DEFAULT_PROJECT_SETTINGS = {
  // Proje Bilgileri
  project: {
    name: 'Yeni Proje',
    type: PROJECT_TYPES.RESIDENTIAL,
    buildingType: BUILDING_TYPES.APARTMENT,
    location: 'İstanbul, Türkiye',
    createdAt: null,
    version: '1.0'
  },

  // Elektriksel Ayarlar
  electrical: {
    // Ana Voltaj Seviyeleri
    mainVoltage: 380,              // Ana gerilim (V)
    singlePhaseVoltage: 220,       // Tek faz gerilim (V)
    frequency: 50,                 // Frekans (Hz)

    // Varsayılan Güç Ayarları
    purposeOfUse: USAGE_PURPOSES.MESKEN,
    demandFactor: '60-40%',
    powerFactor: 0.9,

    // Faz Ayarları
    defaultPhaseCount: 3,
    singlePhaseLimit: 10000,       // 10kW üzeri 3 faz

    // Güvenlik Ayarları
    earthingSystem: 'TN-S',        // Topraklama sistemi
    rcdSensitivity: 30,            // RCD hassasiyeti (mA)

    // Standartlar
    standards: ELECTRICAL_STANDARDS.TSE,
    region: 'Turkey'
  },

  // Component Varsayılan Ayarları
  components: {
    // Main Panel Ayarları
    mainPanel: {
      defaultVoltage: 380,
      defaultPhaseCount: 3,
      ipRating: 'IP54',
      panelType: 'outdoor',
      installationType: 'wall_mounted',
      // Breaker/RCD malzeme ayarları
      breaker: {
        type: 'MCB',
        indexNo: 555,//TipC,3P+N,25A,10kA
        defaultAmperage: 25
      },
      rcd: {
        type: 'RCD',
        indexNo: 9,//300mA 25A 4P
        defaultAmperage: 25,
        sensitivity: 300  // ✅ Ana pano: 300mA yangın koruma
      }
    },

    // Panel Ayarları
    panels: {
      defaultVoltage: 380,
      defaultPhaseCount: 3,
      ipRating: 'IP40',
      panelType: 'enclosed',
      installationType: 'wall_mounted',
      breaker: {
        type: 'MCB',
        indexNo: 423,//TipB,4P,25A,10kA
        defaultAmperage: 25
      },
      rcd: {
        type: 'RCD',
        defaultAmperage: 25,
        indexNo: 3,//30mA 25A 4P
        sensitivity: 30  // ✅ Paneller: 30mA
      }
    },

    // Load Ayarları
    loads: {
      defaultVoltage: 220,
      defaultPhaseCount: 1,
      defaultPowerFactor: 0.9,
      // Makine varsayılan güçleri (W)
      machinePowers: {
        small: 1500,
        medium: 3000,
        large: 5500
      },
      // Priz varsayılan güçleri (W)
      socketPowers: {
        standard: 16 * 220,        // 16A * 220V = 3520W
        heavy: 20 * 220            // 20A * 220V = 4400W
      }
    }
  },

  // Feeder Varsayılan Ayarları
  feeders: {
    // Main Feeder (Ana Besleyici: supply → meter_panel)
    // ⚠️ indexNo'lar kullanıcıya bağlı olduğu için burada tanımlanmaz
    // Factory'de phaseCount'a göre otomatik hesaplanır
    mainFeeder: {
      voltage: 380,
      phaseCount: 3,
      cable: {
        // indexNo kaldırıldı - factory'de hesaplanacak
        installationType: 'underground'
      },
      protection: {
        mcb: {
          // indexNo kaldırıldı - factory'de hesaplanacak
          defaultAmperage: 25
        },
        rcd: {
          // indexNo kaldırıldı - factory'de hesaplanacak
          sensitivity: 300  // ✅ Main feeder: 300mA yangın koruma
        }
      }
    },

    // Dist Feeder (Dağıtım Besleyici: meter_panel → dist_panel)
    distFeeder: {
      voltage: 380,
      phaseCount: 3,
      cable: {
        // indexNo kaldırıldı - factory'de hesaplanacak
        installationType: 'cable_tray'
      },
      protection: {
        mcb: {
          // indexNo kaldırıldı - factory'de hesaplanacak
          defaultAmperage: 25
        },
        rcd: {
          // indexNo kaldırıldı - factory'de hesaplanacak
          sensitivity: 30  // ✅ Dist feeder: 30mA
        }
      }
    },

    // Panel Feeder (Panel Besleyici: dist_panel → panel)
    panelFeeder: {
      voltage: 380,
      phaseCount: 3,
      cable: {
        // indexNo kaldırıldı - factory'de hesaplanacak
        installationType: 'cable_tray'
      },
      protection: {
        mcb: {
          // indexNo kaldırıldı - factory'de hesaplanacak
          defaultAmperage: 25
        },
        rcd: {
          // indexNo kaldırıldı - factory'de hesaplanacak
          sensitivity: 30  // ✅ Panel feeder: 30mA
        }
      }
    },

    // Branch Circuit Feeders (Panel → Load)
    branchCircuits: {
      // Socket Circuit (Priz Devresi)
      socketCircuit: {
        voltage: 220,
        phaseCount: 1,
        cable: {
          // indexNo kaldırıldı - factory'de hesaplanacak
          installationType: 'conduit'
        },
        protection: {
          mcb: {
            // indexNo kaldırıldı - factory'de hesaplanacak
            defaultAmperage: 16
          },
          rcd: {
            // indexNo kaldırıldı - factory'de hesaplanacak
            sensitivity: 30  // ✅ Socket circuit: 30mA
          }
        }
      },

      // Lighting Circuit (Aydınlatma Devresi)
      lightingCircuit: {
        voltage: 220,
        phaseCount: 1,
        cable: {
          // indexNo kaldırıldı - factory'de hesaplanacak
          installationType: 'conduit'
        },
        protection: {
          mcb: {
            // indexNo kaldırıldı - factory'de hesaplanacak
            defaultAmperage: 10
          }
        }
      },

      // Machine Circuit (Makine Devresi)
      machineCircuit: {
        voltage: 220,
        phaseCount: 1,
        cable: {
          indexNo: 4, // CABLES[4] =1x2.5mm² NYA'
          installationType: 'pipe'
        },
        protection: {
          mcb: {
            indexNo: 213, // TipB,1P,20A,6kA
            defaultAmperage: 20
          }
        }
      }
    }
  },

  // Kablo ve Feeder Ayarları
  cables: {
    // Varsayılan Kablo Tipleri
    defaultTypes: {
      underground: 'NYY',
      indoor: 'NYM',
      control: 'YSLY'
    },

    // Varsayılan Malzeme
    defaultMaterial: 'Cu',     // copper, aluminum

    // Minimum Kesitler (mm²)
    minimumSections: {
      lighting: 1.5,
      socket: 2.5,
      machine: 2.5,
      panel: 4,
      main: 6
    },

    // Koruma Ayarları
    protection: {
      defaultMCBCurve: 'C',
      defaultRCDType: 'AC',
      defaultRCDSensitivity: 30
    }
  },

  // UI/Display Ayarları
  display: {
    language: 'tr',                // Türkçe
    units: 'metric',               // Metric system
    currency: 'TRY',               // Türk Lirası

    // Görüntüleme Tercihleri
    showPowerFactors: true,
    showCableLengths: true,
    showProtectionDevices: true,

    // Renk Kodları
    colors: {
      supply: '#2196F3',           // Mavi
      panel: '#4CAF50',            // Yeşil
      load: '#FF9800',             // Turuncu
      feeder: '#607D8B'            // Gri
    }
  }
};

// ===============================
// CURRENT PROJECT SETTINGS (Runtime)
// ===============================
let currentProjectSettings = { ...DEFAULT_PROJECT_SETTINGS };

/**
 * Mevcut proje ayarlarını döndürür
 */
export function getProjectSettings() {
  return currentProjectSettings;
}

/**
 * Proje ayarlarını günceller
 */
export function updateProjectSettings(newSettings) {
  currentProjectSettings = {
    ...currentProjectSettings,
    ...newSettings,
    electrical: {
      ...currentProjectSettings.electrical,
      ...newSettings.electrical
    },
    components: {
      ...currentProjectSettings.components,
      ...newSettings.components
    },
    feeders: {
      ...currentProjectSettings.feeders,
      ...newSettings.feeders
    },
    cables: {
      ...currentProjectSettings.cables,
      ...newSettings.cables
    },
    display: {
      ...currentProjectSettings.display,
      ...newSettings.display
    }
  };
 
  return currentProjectSettings;
}

/**
 * Proje ayarlarını sıfırlar
 */
export function resetProjectSettings() {
  currentProjectSettings = { ...DEFAULT_PROJECT_SETTINGS };
  return currentProjectSettings;
}

/**
 * Belirli bir kullanım amacı için önceden tanımlı ayarları döndürür
 */
export function getPresetForUsagePurpose(purposeOfUse) {
  const presets = {
    [USAGE_PURPOSES.MESKEN]: {
      electrical: {
        purposeOfUse: USAGE_PURPOSES.MESKEN,
        demandFactor: '60-40%',
        mainVoltage: 380,
        singlePhaseVoltage: 220
      }
    },
    [USAGE_PURPOSES.ISYERI]: {
      electrical: {
        purposeOfUse: USAGE_PURPOSES.ISYERI,
        demandFactor: '100-50%',
        mainVoltage: 380,
        singlePhaseVoltage: 220
      }
    },
    [USAGE_PURPOSES.GT]: {
      electrical: {
        purposeOfUse: USAGE_PURPOSES.GT,
        demandFactor: '100%',
        mainVoltage: 380,
        singlePhaseVoltage: 220
      }
    },
    [USAGE_PURPOSES.ASANSOR]: {
      electrical: {
        purposeOfUse: USAGE_PURPOSES.ASANSOR,
        demandFactor: '55%',
        mainVoltage: 380,
        singlePhaseVoltage: 220
      }
    }
  };

  return presets[purposeOfUse] || presets[USAGE_PURPOSES.MESKEN];
}

/**
 * Proje tipine göre önceden tanımlı ayarları döndürür
 */
export function getPresetForProjectType(projectType) {
  const presets = {
    [PROJECT_TYPES.RESIDENTIAL]: {
      electrical: {
        purposeOfUse: USAGE_PURPOSES.MESKEN,
        demandFactor: '60-40%'
      },
      components: {
        loads: {
          machinePowers: {
            small: 1500,
            medium: 2200,
            large: 3700
          }
        }
      }
    },
    [PROJECT_TYPES.COMMERCIAL]: {
      electrical: {
        purposeOfUse: USAGE_PURPOSES.ISYERI,
        demandFactor: '100-50%'
      },
      components: {
        loads: {
          machinePowers: {
            small: 2200,
            medium: 5500,
            large: 11000
          }
        }
      }
    },
    [PROJECT_TYPES.INDUSTRIAL]: {
      electrical: {
        purposeOfUse: USAGE_PURPOSES.GT,
        demandFactor: '100%'
      },
      components: {
        loads: {
          machinePowers: {
            small: 5500,
            medium: 11000,
            large: 22000
          }
        }
      }
    }
  };

  return presets[projectType] || presets[PROJECT_TYPES.RESIDENTIAL];
}